import { Dialog as D } from "radix-ui";
import { useEffect, useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import { create } from "zustand";
import { cn } from "../../lib/cn";
import { IconX } from "../icons";
import { Button } from "./Button";

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  className,
  hideTitle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  hideTitle?: boolean;
}) {
  const width = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-xl", xl: "max-w-3xl" }[size];
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="motion-overlay fixed inset-0 z-50 bg-overlay" />
        <D.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex max-h-[min(88dvh,820px)] w-[calc(100vw-24px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl bg-bg-subtle shadow-pop motion-modal focus:outline-none",
            width,
            className,
          )}
          onOpenAutoFocus={(e) => {
            // Focus the first input if there is one, otherwise the dialog itself.
            const el = (e.currentTarget as HTMLElement).querySelector<HTMLElement>("input:not([type=hidden]),textarea,[data-autofocus]");
            if (el) {
              e.preventDefault();
              el.focus();
            }
          }}
        >
          <div className={cn("flex items-start justify-between gap-4 px-5 pt-4", hideTitle && "sr-only")}>
            <div className="min-w-0">
              <D.Title className="text-lg font-semibold text-fg">{title}</D.Title>
              {description ? (
                <D.Description className="mt-1 text-sm text-fg-2">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{typeof title === "string" ? title : "Dialog"}</D.Description>
              )}
            </div>
            <D.Close className="-mr-2 -mt-0.5 rounded-md p-1.5 text-fg-3 hover:bg-surface hover:text-fg" aria-label="Close">
              <IconX className="icon" />
            </D.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-4">{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/* ───────────── "Are you sure?" — one modal for every confirmation ───────────── */

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Shown in a tinted tile above the title. */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** What will happen, as a short list. */
  consequences?: ReactNode[];
  /** The user must type this exactly before confirming (e.g. a community's name). */
  requireText?: string;
  /** An optional extra choice, returned as `checked`. */
  checkbox?: { label: ReactNode; defaultChecked?: boolean };
}

export interface ConfirmResult {
  confirmed: boolean;
  checked: boolean;
}

interface ConfirmState {
  open: boolean;
  opts: ConfirmOptions | null;
  resolve: ((v: ConfirmResult) => void) | null;
  ask: (o: ConfirmOptions) => Promise<ConfirmResult>;
  close: (v: ConfirmResult) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  open: false,
  opts: null,
  resolve: null,
  ask: (opts) =>
    new Promise<ConfirmResult>((resolve) => {
      get().resolve?.({ confirmed: false, checked: false });
      set({ open: true, opts, resolve });
    }),
  close: (v) => {
    get().resolve?.(v);
    set({ open: false, resolve: null });
  },
}));

/** Full result, including the optional checkbox. */
export const confirmWith = (o: ConfirmOptions) => useConfirmStore.getState().ask(o);
/** Resolves true when the user confirms. */
export const confirm = (o: ConfirmOptions) => confirmWith(o).then((r) => r.confirmed);

export function ConfirmHost() {
  const { open, opts, close } = useConfirmStore();
  const [typed, setTyped] = useState("");
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    if (open) {
      setTyped("");
      setChecked(!!opts?.checkbox?.defaultChecked);
    }
  }, [open, opts]);
  const needsText = !!opts?.requireText;
  const textOk = !needsText || typed.trim() === opts!.requireText!.trim();
  const Icon = opts?.icon;
  const done = (confirmed: boolean) => close({ confirmed: confirmed && textOk, checked });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && done(false)}
      title={
        <span className="flex flex-col gap-3">
          {Icon && (
            <span className={cn("flex size-10 items-center justify-center rounded-xl", opts?.danger ? "bg-danger-soft text-danger" : "bg-surface-2 text-fg-2")}>
              <Icon className="icon size-5" aria-hidden />
            </span>
          )}
          {opts?.title ?? ""}
        </span>
      }
      description={opts?.description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => done(false)}>
            {opts?.cancelLabel ?? "Cancel"}
          </Button>
          <Button variant={opts?.danger ? "danger" : "primary"} disabled={!textOk} onClick={() => done(true)} data-autofocus={needsText ? undefined : true}>
            {opts?.confirmLabel ?? "Confirm"}
          </Button>
        </>
      }
    >
      {(opts?.consequences?.length || needsText || opts?.checkbox) && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (textOk) done(true);
          }}
        >
          {!!opts?.consequences?.length && (
            <ul className="space-y-1.5 rounded-lg bg-surface px-3 py-2.5 text-sm text-fg-2">
              {opts.consequences.map((c, i) => (
                <li key={i} className="flex gap-2">
                  <span className={cn("mt-[7px] size-1.5 shrink-0 rounded-full", opts.danger ? "bg-danger" : "bg-fg-3")} aria-hidden />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          )}
          {needsText && (
            <label className="block space-y-1.5">
              <span className="text-sm text-fg-2">
                Type <span className="font-semibold text-fg">{opts!.requireText}</span> to confirm
              </span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="h-10 w-full rounded-md border border-line-strong bg-surface px-3 text-base text-fg focus:border-accent focus:outline-none"
              />
            </label>
          )}
          {opts?.checkbox && (
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-fg-2">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="size-4 accent-[var(--accent)]" />
              {opts.checkbox.label}
            </label>
          )}
        </form>
      )}
    </Dialog>
  );
}
